import React from 'react';
import { Card, Typography, Chip, Progress } from "@material-tailwind/react";

const PayTypeBreakdown = ({ summaryData, loading = false }) => {
    const formatCurrency = (value) => {
        if (!value || value === 0) return '-';
        return new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: 'KES',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(value);
    };

    if (loading) {
        return (
            <Card className="p-6">
                <div className="animate-pulse space-y-4">
                    <div className="h-6 bg-gray-200 rounded w-1/3"></div>
                    <div className="space-y-2">
                        {[...Array(5)].map((_, i) => (
                            <div key={i} className="h-16 bg-gray-200 rounded"></div>
                        ))}
                    </div>
                </div>
            </Card>
        );
    }

    if (!summaryData || summaryData.length === 0) {
        return (
            <Card className="p-6 text-center">
                <Typography variant="h6" color="gray">
                    No transaction data available
                </Typography>
            </Card>
        );
    }



    const formatPercentage = (value) => {
        return `${value.toFixed(1)}%`;
    };

    // Calculate totals for percentage calculations
    const totalAmount = summaryData.reduce((sum, item) => sum + parseFloat(item.net_amount || 0), 0);
    const totalTransactions = summaryData.reduce((sum, item) => sum + parseInt(item.transaction_count || 0), 0);

    const getAmountColor = (amount) => {
        if (amount > 0) return 'text-green-600';
        if (amount < 0) return 'text-red-600';
        return 'text-gray-600';
    };

    const getProgressColor = (percentage) => {
        if (percentage > 40) return 'green';
        if (percentage > 20) return 'blue';
        if (percentage > 10) return 'orange';
        return 'red';
    };

    return (
        <Card className="p-6">
            <div className="flex items-center justify-between mb-6">
                <Typography variant="h6" color="blue-gray">
                    Transaction Type Breakdown
                </Typography>
                <div className="flex gap-2">
                    <Chip
                        size="sm"
                        variant="filled"
                        color="blue"
                        value={`${summaryData.length} Types`}
                    />
                    <Chip
                        size="sm"
                        variant="filled"
                        color="green"
                        value={formatCurrency(totalAmount)}
                    />
                </div>
            </div>

            <div className="space-y-4">
                {summaryData.map((item, index) => {
                    const netAmount = parseFloat(item.net_amount || 0);
                    const transactionCount = parseInt(item.transaction_count || 0);
                    const amountPercentage = totalAmount !== 0 ? (Math.abs(netAmount) / Math.abs(totalAmount)) * 100 : 0;
                    const countPercentage = totalTransactions !== 0 ? (transactionCount / totalTransactions) * 100 : 0;

                    return (
                        <div key={index} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                            <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-3">
                                    <Typography variant="h6" color="blue-gray" className="capitalize">
                                        {item.type_name || item.trans_type}
                                    </Typography>
                                    <Chip
                                        size="sm"
                                        variant="ghost"
                                        color="blue"
                                        value={item.trans_type}
                                        className="uppercase"
                                    />
                                </div>
                                <Typography
                                    variant="h6"
                                    className={`font-bold ${getAmountColor(netAmount)}`}
                                >
                                    {formatCurrency(netAmount)}
                                </Typography>
                            </div>

                            {/* Transaction Count and Amount Details */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                                <div className="text-center">
                                    <Typography variant="small" color="gray" className="text-xs">
                                        Transactions
                                    </Typography>
                                    <Typography variant="small" color="blue-gray" className="font-bold">
                                        {transactionCount}
                                    </Typography>
                                </div>
                                <div className="text-center">
                                    <Typography variant="small" color="gray" className="text-xs">
                                        Total Debit
                                    </Typography>
                                    <Typography variant="small" color="red" className="font-bold">
                                        {formatCurrency(parseFloat(item.total_debit || 0))}
                                    </Typography>
                                </div>
                                <div className="text-center">
                                    <Typography variant="small" color="gray" className="text-xs">
                                        Total Credit
                                    </Typography>
                                    <Typography variant="small" color="green" className="font-bold">
                                        {formatCurrency(parseFloat(item.total_credit || 0))}
                                    </Typography>
                                </div>
                            </div>

                            {/* Progress Bars */}
                            <div className="space-y-2">
                                <div>
                                    <div className="flex justify-between items-center mb-1">
                                        <Typography variant="small" color="gray" className="text-xs">
                                            Amount Share
                                        </Typography>
                                        <Typography variant="small" color="blue-gray" className="text-xs font-medium">
                                            {formatPercentage(amountPercentage)}
                                        </Typography>
                                    </div>
                                    <Progress
                                        value={amountPercentage}
                                        color={getProgressColor(amountPercentage)}
                                        className="h-2"
                                    />
                                </div>

                                <div>
                                    <div className="flex justify-between items-center mb-1">
                                        <Typography variant="small" color="gray" className="text-xs">
                                            Transaction Share
                                        </Typography>
                                        <Typography variant="small" color="blue-gray" className="text-xs font-medium">
                                            {formatPercentage(countPercentage)}
                                        </Typography>
                                    </div>
                                    <Progress
                                        value={countPercentage}
                                        color={getProgressColor(countPercentage)}
                                        className="h-2"
                                    />
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Summary Footer */}
            <div className="mt-6 pt-4 border-t border-gray-200">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                    <div>
                        <Typography variant="small" color="gray">
                            Total Types
                        </Typography>
                        <Typography variant="h6" color="blue-gray" className="font-bold">
                            {summaryData.length}
                        </Typography>
                    </div>
                    <div>
                        <Typography variant="small" color="gray">
                            Total Transactions
                        </Typography>
                        <Typography variant="h6" color="blue-gray" className="font-bold">
                            {totalTransactions}
                        </Typography>
                    </div>
                    <div>
                        <Typography variant="small" color="gray">
                            Net Amount
                        </Typography>
                        <Typography
                            variant="h6"
                            className={`font-bold ${getAmountColor(totalAmount)}`}
                        >
                            {formatCurrency(totalAmount)}
                        </Typography>
                    </div>
                </div>
            </div>
        </Card>
    );
};

export default PayTypeBreakdown;
